<template>
    <v-container fluid class="pb-12">
        <v-card class="packages-table-card">
            <div class="card-section-header px-6 py-4">
                <div class="d-flex align-center flex-wrap" style="gap:10px;">
                    <v-text-field outlined dense hide-details placeholder="Search name" prepend-inner-icon="mdi-magnify"
                        v-model="search_name" clearable style="width:220px;" @input="onSearchInput()"
                        class="header-filter-input">
                    </v-text-field>
                    <v-select outlined dense hide-details placeholder="All Locations" v-model="search_location"
                        :items="get_locations" item-text="name" item-value="name" clearable style="width:200px;"
                        @change="resetAndFetch()" class="header-filter-input">
                    </v-select>
                    <v-select outlined dense hide-details placeholder="All Types" v-model="search_type"
                        :items="[{ text: 'Stag', value: 'stag' }, { text: 'Hen', value: 'hen' }, { text: 'Birthday', value: 'birthday' }]"
                        item-text="text" item-value="value" clearable style="width:160px;" @change="resetAndFetch()"
                        class="header-filter-input">
                    </v-select>
                    <v-spacer></v-spacer>
                    <v-btn outlined depressed class="packages-header-btn header-outline-btn" @click="resetAndFetch()"
                        :loading="searching">
                        <v-icon left>mdi-refresh</v-icon>
                        Refresh
                    </v-btn>
                    <v-btn depressed class="packages-header-btn header-create-btn" @click="createPackage()">
                        <v-icon left>mdi-plus</v-icon>
                        Create
                    </v-btn>
                </div>
                <div v-if="hasActiveFilters" class="header-clear-row">
                    <v-btn x-small text class="header-clear-btn" @click="clearFilters()">
                        <v-icon left x-small>mdi-close</v-icon>
                        Clear filters
                    </v-btn>
                </div>
            </div>

            <!-- Table -->
            <v-data-table :headers="headers" :items="packages" :sort-by.sync="sort_by"
                :sort-desc.sync="sort_desc" :loading="searching" loading-text="Loading packages..."
                :server-items-length="packages_total" :footer-props="{ 'items-per-page-options': [20] }"
                @update:options="onTableOptions">
                <template v-slot:item="{ item }">
                    <tr class="package-row" @click="viewPackage(item)">
                        <!-- Image -->
                        <td class="py-1" style="width:70px;">
                            <v-img v-if="item.image" :src="api_url + 'media/suppliers/images/' + item.image" width="56"
                                height="42" class="rounded" style="object-fit:cover;"></v-img>
                            <div v-else class="image-placeholder rounded">
                                <v-icon color="grey lighten-2" size="22">mdi-image-off-outline</v-icon>
                            </div>
                        </td>
                        <!-- Name / meta -->
                        <td>
                            <span class="font-weight-medium">{{ item.name }}</span>
                            <div class="d-flex align-center mt-1" style="gap:6px;">
                                <v-chip x-small label v-if="item.type"
                                    :color="item.type === 'hen' ? 'pink lighten-4' : item.type === 'birthday' ? 'green lighten-4' : 'blue lighten-4'"
                                    :text-color="item.type === 'hen' ? 'pink darken-3' : item.type === 'birthday' ? 'green darken-3' : 'blue darken-3'"
                                    class="font-weight-medium text-capitalize">
                                    {{ item.type }}
                                </v-chip>
                                <span class="grey--text caption" v-if="item.nights != null">{{ item.nights }} night{{
                                    item.nights !== 1 ? 's' : '' }}</span>
                                <span class="grey--text caption" v-if="item.total_price">
                                    &bull; {{ get_selected_company.currency }}{{ item.total_price }}pp
                                </span>
                            </div>
                            <div v-if="item.short_description" class="grey--text mt-1"
                                style="font-size:11px;line-height:1.4;max-width:400px;" v-html="item.short_description">
                            </div>
                        </td>
                        <!-- Location -->
                        <td>
                            <span v-if="item.location">{{ item.location.name }}</span>
                            <span v-else class="grey--text">—</span>
                        </td>
                        <!-- Day Activity / Night Activity / Dinner / Fingerfood -->
                        <td>
                            <div v-for="group in groupedBuilds(item)" :key="group.label" class="mb-1">
                                <div class="build-group-label">{{ group.label }}</div>
                                <div v-for="(build, index) in group.builds" :key="index" class="build-entry">
                                    <v-chip v-if="index === 0 || group.builds[index - 1].day !== build.day"
                                        x-small label :color="group.color + ' lighten-5'"
                                        :text-color="group.color + ' darken-2'" style="align-self:flex-start;">
                                        Day {{ build.day }}
                                    </v-chip>
                                    <span v-else class="mr-1" style="display:inline-block;width:42px;"></span>
                                    <span class="caption">{{ buildLabel(build, group) }}</span>
                                </div>
                            </div>
                            <span v-if="!groupedBuilds(item).length" class="grey--text caption">—</span>
                        </td>
                        <!-- Actions -->
                        <td @click.stop class="py-1">
                            <v-tooltip bottom color="primary">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-btn small icon v-bind="attrs" v-on="on" @click="viewPackage(item)">
                                        <v-icon small color="primary">mdi-pen</v-icon>
                                    </v-btn>
                                </template>
                                <span>View/Update Package</span>
                            </v-tooltip>
                            <v-tooltip bottom color="error">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-btn small icon v-bind="attrs" v-on="on" @click="deletePackageDialog(item)">
                                        <v-icon small color="error">mdi-delete</v-icon>
                                    </v-btn>
                                </template>
                                <span>Delete Package</span>
                            </v-tooltip>
                        </td>
                    </tr>
                </template>
                <template v-slot:no-data>
                    <div class="py-10 text-center">
                        <v-icon size="48" color="grey lighten-2">mdi-package-variant-closed</v-icon>
                        <div class="mt-2 grey--text">No packages found</div>
                    </div>
                </template>
            </v-data-table>
        </v-card>

        <ConfirmDeletePackage :delete_package_dialog="delete_package_dialog"
            @closeDeletePackageDialog="closeDeletePackageDialog()" @deletePackage="deletePackage()">
        </ConfirmDeletePackage>
    </v-container>
</template>

<script>
import { mapGetters, mapActions } from "vuex"
import ConfirmDeletePackage from './components/ConfirmDeletePackage.vue'
import pageBadgesMixin from '@/common/PageNameStore/pageBadgesMixin'
import { lookups, loadLookups } from '@/common/companyLookups'
export default {
    mixins: [pageBadgesMixin],
    data: () => ({
        classes: {
        },
        searching: false,
        original_packages: [],
        packages: [],
        packages_total: 0,
        current_page: 1,
        _search_timer: null,
        headers: [
            { text: '', value: 'image', sortable: false, width: '70px' },
            { text: 'Name', value: 'name', width: '40%' },
            { text: 'Location', value: 'location.name', width: '15%' },
            { text: 'Activities/Food/Other', sortable: false, width: '35%' },
            { text: 'Action', value: 'action', sortable: false, width: '80px' },
        ],
        delete_package_dialog: false,
        delete_package_id: null,
        sort_by: 'name',
        sort_desc: false,
        packages_file: null,
        package_builds_file: null,
        search_name: null,
        search_location: null,
        search_type: null,
        api_url: null,
    }),
    computed: {
        get_locations() {
            return lookups.locations
        },
        ...mapGetters({
            get_selected_company: 'auth/get_selected_company',
        }),
        hasActiveFilters() {
            return !!(this.search_location || this.search_name || this.search_type)
        },
        pageBadges() {
            return [this.packages_total.toLocaleString()]
        },
    },
    watch: {
        search_type() { this.resetAndFetch() },
        search_location() { this.resetAndFetch() },
    },
    components: {
        ConfirmDeletePackage
    },
    created() {
        this.setPageName("Packages")
    },
    async mounted() {
        this.api_url = process.env.VUE_APP_API_URL
        this.fetchPackages()
        loadLookups('locations')
    },
    methods: {
        ...mapActions({
            setPageName: 'pageNameStore/setPageNameAction',
        }),
        // Day Activity, Night Activity, Dinner and Fingerfood share one column, grouped by type.
        groupedBuilds(item) {
            const groups = [
                { type_id: 2, label: 'Day Activity', color: 'blue' },
                { type_id: 6, label: 'Night Activity', color: 'purple' },
                { type_id: 5, label: 'Dinner', color: 'orange' },
                { type_id: 4, label: 'Finger Food', color: 'green' },
            ]
            return groups
                .map(g => ({ ...g, builds: (item.builds || []).filter(b => b.type_id == g.type_id) }))
                .filter(g => g.builds.length > 0)
        },
        buildLabel(build, group) {
            // Night Activity and Finger Food show the supplier only; the others show the chosen option when there is one.
            const supplierOnly = group.type_id === 6 || group.type_id === 4
            return !supplierOnly && build.option ? build.option.name : build.supplier.supplier_name
        },
        filterPackages() {
            // kept for compatibility — filters now trigger server fetches via watchers/events
        },
        clearFilters() {
            this.search_location = null
            this.search_name = null
            this.search_type = null
            this.fetchPackages(1)
        },
        resetAndFetch() {
            this.fetchPackages(1)
        },
        onSearchInput() {
            clearTimeout(this._search_timer)
            this._search_timer = setTimeout(() => this.fetchPackages(1), 350)
        },
        onTableOptions(options) {
            this.fetchPackages(options.page)
        },
        resetPackages() {
            this.clearFilters()
        },
        async importPackages() {
            if (this.packages_file) {
                const formData = new FormData()
                formData.append('file', this.packages_file)
                formData.append('company_id', this.get_selected_company.id)
                await this.$axios.post('admin/packages/import_packages', formData)
                    .then(({ data }) => {
                        console.log(data)
                        this.packages_file = null
                    })
            }
        },
        async importPackageBuilds() {
            if (this.package_builds_file) {
                const formData = new FormData()
                formData.append('file', this.package_builds_file)
                formData.append('company_id', this.get_selected_company.id)
                await this.$axios.post('admin/packages/import_package_builds', formData)
                    .then(({ data }) => {
                        console.log(data)
                        this.package_builds_file = null
                    })
            }
        },
        viewPackage(item) {
            this.$router.push({ name: 'EditPackage', params: { package_id: item.id } })
        },
        deletePackageDialog(item) {
            this.delete_package_dialog = true
            this.delete_package_id = item.id
        },
        closeDeletePackageDialog() {
            this.delete_package_dialog = false
        },
        async deletePackage() {
            const response = await this.$axios.delete(`admin/packages/delete_package/${this.delete_package_id}`)
            const result = response.data
            if (result.response == true) {
                this.$toast.success(result.message, { timeout: 2000, icon: true })
                this.closeDeletePackageDialog()
                this.fetchPackages()
            }
            else {
                this.$toast.error(result.message)
            }
        },
        async fetchPackages(page = 1) {
            this.searching = true
            this.current_page = page
            try {
                const params = { page, per_page: 20 }
                if (this.search_name) params.search = this.search_name
                if (this.search_location) params.location = this.search_location
                if (this.search_type) params.type = this.search_type
                const response = await this.$axios.get(
                    `admin/packages/get_packages_paginated/${this.get_selected_company.id}`,
                    params
                )
                const result = response.data
                if (result.response) {
                    this.packages = result.data
                    this.packages_total = result.total
                } else {
                    this.packages = []
                    this.packages_total = 0
                }
            } catch (_) {
                this.packages = []
                this.packages_total = 0
            }
            this.searching = false
        },
        createPackage() {
            this.$router.push({ name: 'CreatePackage' })
        },
    }
}
</script>

<style lang="scss" scoped>
.card-section-header {
    background: linear-gradient(135deg, #3988AA 0%, #2a6a88 100%);
}

.header-filter-input ::v-deep .v-input__control {
    background: white;
    border-radius: 4px;
}

.header-clear-row {
    margin: 6px 0 -8px -8px;
}

.header-clear-btn {
    font-family: Manrope;
    font-weight: 600 !important;
    font-size: 12px !important;
    text-transform: none;
    color: white !important;
}

.header-outline-btn {
    background: rgba(255, 255, 255, 0.15) !important;
    color: white !important;
    border-color: rgba(255, 255, 255, 0.5) !important;
}

.header-create-btn {
    background: white !important;
    color: #3988AA !important;
}

.packages-header-btn {
    font-family: Manrope;
    font-weight: 600 !important;
    font-size: 13px !important;
    text-transform: none;
    height: 36px !important;
    padding: 0 14px !important;
}

.packages-table-card {
    border-radius: 10px !important;
    border: 1px solid #E9E9E9 !important;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06) !important;
    overflow: hidden;
}

:deep(.packages-table-card .v-data-footer) {
    background-color: #F9F9F9;
    color: #7C7C7C;
    font-family: Manrope;
    border-top: 1px solid #E9E9E9;
}

.package-row {
    cursor: pointer;
    transition: background-color 0.15s ease;

    &:hover {
        background-color: #F5FAFD !important;
    }

    td {
        font-family: Manrope;
        font-size: 0.875rem;
    }
}

.build-entry {
    display: flex;
    align-items: center;
    gap: 4px;
    margin-bottom: 2px;
}

.build-group-label {
    font-size: 10px;
    text-transform: uppercase;
    color: #6b6b6b;
}

.image-placeholder {
    width: 56px;
    height: 42px;
    background: #F5F5F5;
    display: flex;
    align-items: center;
    justify-content: center;
}
</style>